import type { Event } from "nostr-tools/pure";
import type { RandomnessBeacon } from "drand-client";
import { parseManifest, type ManifestFile } from "./manifest.js";
import { KIND_GIFT_WRAP, KIND_TIME_CAPSULE } from "./validate.js";

/**
 * A capsule kept outside vrelis.com (PLAN.md 4.8): the original signed event, and after unlock
 * optionally the drand beacon of its round (then it no longer needs drand) and the opened text.
 * Read by the offline opener; written by the web app's download.
 *
 *   public:  event = the signed kind 1041
 *   private: event = the kind 1059 wrap addressed to the member (opened with the recovery phrase)
 */
export const CAPSULE_FILE_FORMAT = "vrelis-capsule-v1";

export type CapsuleFile = {
  format: typeof CAPSULE_FILE_FORMAT;
  event: Event;
  /** drand quicknet beacon of the capsule's round; readers verify it before use. */
  beacon?: RandomnessBeacon;
  /** The opened text and file entries, saved by the member's choice. Plain on disk; readers say it is unverified. */
  opened?: { text: string; files: ManifestFile[] };
};

export class CapsuleFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CapsuleFileError";
  }
}

export function makeCapsuleFile(event: Event, extra: { beacon?: RandomnessBeacon; opened?: { text: string; files: ManifestFile[] } } = {}): CapsuleFile {
  const { id, pubkey, created_at, kind, tags, content, sig } = event;
  return {
    format: CAPSULE_FILE_FORMAT,
    event: { id, pubkey, created_at, kind, tags, content, sig },
    ...(extra.beacon ? { beacon: { round: extra.beacon.round, randomness: extra.beacon.randomness, signature: extra.beacon.signature } as RandomnessBeacon } : {}),
    ...(extra.opened ? { opened: extra.opened } : {}),
  };
}

function asEvent(x: unknown): Event {
  if (typeof x !== "object" || x === null) throw new CapsuleFileError("no event in the file");
  const e = x as Record<string, unknown>;
  const ok =
    typeof e.id === "string" &&
    typeof e.pubkey === "string" &&
    typeof e.sig === "string" &&
    typeof e.content === "string" &&
    Number.isSafeInteger(e.created_at) &&
    (e.kind === KIND_TIME_CAPSULE || e.kind === KIND_GIFT_WRAP) &&
    Array.isArray(e.tags) &&
    e.tags.every((t) => Array.isArray(t) && t.every((v) => typeof v === "string"));
  if (!ok) throw new CapsuleFileError("the event is not a time capsule (kind 1041) or a private wrap (kind 1059)");
  const { id, pubkey, created_at, kind, tags, content, sig } = e as Event;
  return { id, pubkey, created_at, kind, tags, content, sig };
}

/**
 * Reads a capsule file, or a bare Nostr event (kind 1041 or 1059). Signatures, stanza and beacon
 * are not checked here: opening does that, as for any capsule.
 */
export function parseCapsuleFile(json: string): CapsuleFile {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new CapsuleFileError("not a JSON file");
  }
  if (typeof raw !== "object" || raw === null) throw new CapsuleFileError("not a capsule file");
  const r = raw as Record<string, unknown>;
  if (r.format === undefined) return { format: CAPSULE_FILE_FORMAT, event: asEvent(raw) };
  if (r.format !== CAPSULE_FILE_FORMAT) throw new CapsuleFileError(`unknown format ${String(r.format)}`);
  const file: CapsuleFile = { format: CAPSULE_FILE_FORMAT, event: asEvent(r.event) };
  if (r.beacon !== undefined) {
    const b = r.beacon as Record<string, unknown>;
    if (typeof b !== "object" || b === null || !Number.isSafeInteger(b.round) || typeof b.signature !== "string") throw new CapsuleFileError("malformed beacon");
    file.beacon = { round: b.round as number, signature: b.signature, randomness: typeof b.randomness === "string" ? b.randomness : "" } as RandomnessBeacon;
  }
  if (r.opened !== undefined) {
    const o = r.opened as Record<string, unknown>;
    if (typeof o !== "object" || o === null || typeof o.text !== "string") throw new CapsuleFileError("malformed opened text");
    file.opened = { text: o.text, files: parseManifest(JSON.stringify({ v: 1, text: o.text, files: o.files ?? [] })).files };
  }
  return file;
}
